import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { EntityKind, ReceiptListItem, receipts, VendorSummary } from "../api/client";
import { formatReceiptDate, receiptDay } from "../receiptDate";

type SortKey = "received_at" | "subject" | "card_last4" | "amount_cents";
type SortDir = "asc" | "desc";

function formatCents(cents?: number | null, currency?: string | null): string {
  if (cents == null) return "—";
  const value = cents / 100;
  try {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: currency || "USD",
    });
  } catch {
    return value.toFixed(2);
  }
}

function sortValue(r: ReceiptListItem, key: SortKey): number | string {
  switch (key) {
    case "received_at":
      return receiptDay(r)?.getTime() ?? 0;
    case "subject":
      return (r.subject || "").toLowerCase();
    case "card_last4":
      return r.card_last4 || "";
    case "amount_cents":
      return r.amount_cents ?? -Infinity;
  }
}

function compare(a: ReceiptListItem, b: ReceiptListItem, key: SortKey, dir: SortDir): number {
  const ar = sortValue(a, key);
  const br = sortValue(b, key);
  let n: number;
  if (typeof ar === "number" && typeof br === "number") n = ar - br;
  else n = String(ar).localeCompare(String(br), undefined, { sensitivity: "base" });
  return dir === "asc" ? n : -n;
}

// Route param shape: /app/vendors/name:<encoded vendor_name> — the same field
// the vendor list groups by, so the count + title always agree.

// Common billing currencies offered in the bulk-set picker. The vendor's
// current dominant currency is preselected when the page loads.
const CURRENCY_OPTIONS = ["USD", "EUR", "GBP", "CAD", "AUD", "CHF", "JPY", "SEK", "NOK", "DKK"];

export default function VendorDetail({ kind = "vendor" }: { kind?: EntityKind }) {
  const navigate = useNavigate();
  const { vendor = "" } = useParams<{ vendor: string }>();
  // Wording + routing differ for the Clients (income) view vs Vendors.
  const entityLabel = kind === "client" ? "Client" : "Vendor";
  const noun = entityLabel.toLowerCase();
  const pluralLabel = `${entityLabel}s`;
  const basePath = kind === "client" ? "/app/clients" : "/app/vendors";
  const vendorName = vendor.startsWith("name:")
    ? decodeURIComponent(vendor.slice("name:".length))
    : decodeURIComponent(vendor);

  const [items, setItems] = useState<ReceiptListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("received_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  // Bulk "set currency for all receipts of this vendor" controls.
  const [bulkCurrency, setBulkCurrency] = useState("");
  const [applying, setApplying] = useState(false);
  const [applyMsg, setApplyMsg] = useState<string | null>(null);

  useEffect(() => {
    receipts
      .list({ vendor_name: vendorName, kind, limit: 1000 })
      .then((rows) => {
        setItems(rows);
        // Preselect the vendor's current dominant currency, if any.
        const existing = rows.find((r) => r.currency)?.currency;
        if (existing) setBulkCurrency(existing.toUpperCase());
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load"))
      .finally(() => setLoading(false));
  }, [vendor, vendorName, kind]);

  async function applyCurrency() {
    if (!bulkCurrency || applying) return;
    setApplying(true);
    setApplyMsg(null);
    try {
      const res = await receipts.setCurrencyForVendor(vendorName, bulkCurrency);
      // Reflect the change locally without a refetch.
      setItems((prev) => prev.map((r) => ({ ...r, currency: bulkCurrency })));
      setApplyMsg(
        `Set ${res.updated} receipt${res.updated === 1 ? "" : "s"} to ${bulkCurrency}.`
      );
    } catch (e) {
      setApplyMsg(e instanceof Error ? e.message : "Could not update currency");
    } finally {
      setApplying(false);
    }
  }

  const title = vendorName || entityLabel;

  // ── Rename + Combine ────────────────────────────────────────────────
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState("");
  const [savingName, setSavingName] = useState(false);

  // Combine: lazily-loaded list of other vendors to merge this one into.
  const [combining, setCombining] = useState(false);
  const [vendorList, setVendorList] = useState<VendorSummary[] | null>(null);
  const [destName, setDestName] = useState("");
  const [merging, setMerging] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);

  function startRename() {
    setNewName(title);
    setRenaming(true);
    setActionErr(null);
    setActionMsg(null);
  }

  async function saveRename() {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === title) {
      setRenaming(false);
      return;
    }
    setSavingName(true);
    setActionErr(null);
    try {
      const res = await receipts.renameVendor(vendorName, trimmed);
      setActionMsg(
        `Renamed ${res.updated} receipt${res.updated === 1 ? "" : "s"} to "${trimmed}".`
      );
      setRenaming(false);
      // The name is the route key — navigate to the new canonical URL.
      navigate(`${basePath}/name:${encodeURIComponent(trimmed)}`, { replace: true });
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : "Could not rename vendor");
    } finally {
      setSavingName(false);
    }
  }

  async function openCombine() {
    setCombining(true);
    setDestName("");
    setActionErr(null);
    setActionMsg(null);
    if (vendorList === null) {
      try {
        setVendorList(await receipts.listVendors(kind));
      } catch (e) {
        setActionErr(e instanceof Error ? e.message : `Could not load ${noun}s`);
      }
    }
  }

  async function confirmCombine() {
    const dest = (vendorList ?? []).find((v) => v.vendor_name === destName);
    if (!dest) {
      setActionErr("Choose a destination vendor.");
      return;
    }
    if (
      !confirm(
        `Combine "${title}" into "${dest.vendor_name}"?\n\n` +
          `All ${items.length} receipt(s) on "${title}" will be moved to ` +
          `"${dest.vendor_name}", and "${title}" will be removed.\n\n` +
          `This cannot be undone.`
      )
    ) {
      return;
    }
    setMerging(true);
    setActionErr(null);
    try {
      const res = await receipts.mergeVendors(vendorName, dest.vendor_name);
      // Source vendor is gone — go to the destination vendor's page.
      const destParam = `name:${encodeURIComponent(dest.vendor_name)}`;
      navigate(`${basePath}/${destParam}`, {
        replace: true,
        state: { combinedMsg: `Moved ${res.moved} receipt(s) into "${dest.vendor_name}".` },
      });
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : "Could not combine vendors");
      setMerging(false);
    }
  }

  // Reset the action UI whenever we switch vendors (the component instance is
  // reused across route-param changes). Also surface the "combined" toast
  // handed over by a merge redirect.
  const location = useLocation();
  useEffect(() => {
    setRenaming(false);
    setCombining(false);
    setMerging(false);
    setVendorList(null);
    setActionErr(null);
    const msg = (location.state as { combinedMsg?: string } | null)?.combinedMsg;
    setActionMsg(msg ?? null);
    if (msg) window.history.replaceState({}, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor]);

  // Destinations = every other vendor (can't combine a vendor into itself).
  const destinations = (vendorList ?? []).filter((v) => v.vendor_name !== title);

  const sorted = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const base = needle
      ? items.filter(
          (r) =>
            (r.subject || "").toLowerCase().includes(needle) ||
            (r.sender || "").toLowerCase().includes(needle)
        )
      : items;
    return [...base].sort((a, b) => compare(a, b, sortKey, sortDir));
  }, [items, filter, sortKey, sortDir]);

  function toggleSort(next: SortKey) {
    if (sortKey === next) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(next);
      setSortDir(next === "amount_cents" || next === "received_at" ? "desc" : "asc");
    }
  }

  function sortArrow(col: SortKey): string {
    if (sortKey !== col) return "";
    return sortDir === "asc" ? " ▲" : " ▼";
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link to={basePath} className="muted">← {pluralLabel}</Link>
          <h1 style={{ marginTop: 6 }}>
            {title}{" "}
            {!renaming && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={startRename}
                style={{ verticalAlign: "middle" }}
              >
                Rename
              </button>
            )}
            {!combining && !renaming && (
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={openCombine}
                style={{ verticalAlign: "middle" }}
                title={`Combine this ${noun} into another`}
              >
                Combine
              </button>
            )}
          </h1>
          {renaming && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={savingName}
                autoFocus
                aria-label="New vendor name"
                style={{ minWidth: 240 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveRename();
                  if (e.key === "Escape") setRenaming(false);
                }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={saveRename}
                disabled={savingName || !newName.trim()}
              >
                {savingName ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setRenaming(false)}
                disabled={savingName}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {actionMsg && (
        <div className="muted" style={{ marginBottom: 12 }}>
          {actionMsg}
        </div>
      )}
      {actionErr && <div className="page-error">{actionErr}</div>}

      {combining && (
        <div className="detail-card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>Combine "{title}"</h3>
          <p className="page-sub" style={{ marginTop: 0 }}>
            Move all {items.length} receipt(s) from <strong>{title}</strong> onto
            another {noun}. The receipts keep all their data — only the {noun}
            they're filed under changes.
          </p>
          <div className="page-error" style={{ marginBottom: 12 }} role="alert">
            ⚠ This is irreversible. "{title}" will be permanently removed and its
            receipts re-stamped with the destination {noun}. There is no undo.
          </div>
          {vendorList === null ? (
            <div className="page-empty">Loading vendors…</div>
          ) : destinations.length === 0 ? (
            <div className="page-empty">
              No other {noun} to combine into. You need at least two {noun}s.
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <label htmlFor="combine-dest">Combine into:</label>
              <select
                id="combine-dest"
                value={destName}
                onChange={(e) => setDestName(e.target.value)}
                disabled={merging}
              >
                <option value="">Select destination {noun}…</option>
                {destinations.map((v) => (
                  <option key={v.vendor_name} value={v.vendor_name}>
                    {v.vendor_name} ({v.receipt_count})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={confirmCombine}
                disabled={merging || !destName}
              >
                {merging ? "Combining…" : `Combine ${noun}s`}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setCombining(false)}
                disabled={merging}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      <div className="filter-bar">
        <label style={{ flex: 1 }}>
          Subject / sender
          <input
            type="search"
            placeholder="Filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </label>
        <label>
          Set currency for all receipts
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              value={bulkCurrency}
              onChange={(e) => setBulkCurrency(e.target.value)}
            >
              <option value="">Currency…</option>
              {(CURRENCY_OPTIONS.includes(bulkCurrency) || !bulkCurrency
                ? CURRENCY_OPTIONS
                : [bulkCurrency, ...CURRENCY_OPTIONS]
              ).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!bulkCurrency || applying || items.length === 0}
              onClick={applyCurrency}
            >
              {applying ? "Applying…" : `Apply to all (${items.length})`}
            </button>
          </div>
        </label>
      </div>
      {applyMsg && (
        <div className="muted" style={{ marginBottom: 12 }}>
          {applyMsg}
        </div>
      )}

      {loading ? (
        <div className="page-empty">Loading…</div>
      ) : error ? (
        <div className="page-error">{error}</div>
      ) : sorted.length === 0 ? (
        <div className="page-empty">No receipts for this {noun}.</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th
                onClick={() => toggleSort("received_at")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Date{sortArrow("received_at")}
              </th>
              <th
                onClick={() => toggleSort("subject")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Subject{sortArrow("subject")}
              </th>
              <th
                onClick={() => toggleSort("card_last4")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Card{sortArrow("card_last4")}
              </th>
              <th
                onClick={() => toggleSort("amount_cents")}
                style={{ cursor: "pointer", userSelect: "none", textAlign: "right" }}
              >
                Amount{sortArrow("amount_cents")}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr
                key={r.id}
                className="clickable"
                onClick={() => navigate(`/app/receipts/${r.id}`)}
              >
                <td>{formatReceiptDate(r)}</td>
                <td
                  style={{
                    maxWidth: 360,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {r.subject || "(no subject)"}
                </td>
                <td>
                  {r.card_last4 ? `${r.card_brand ?? "Card"} ···· ${r.card_last4}` : "—"}
                </td>
                <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                  {formatCents(r.amount_cents, r.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
